import moment from 'moment';

// The whole print stylesheet. The record prints in its own iframe with none of
// the dashboard's styles (no theme tokens, dark mode, or scroll containers), so
// everything it needs lives here. Senders are told apart by labels and rules
// rather than background colours, which most printers leave out.
export const PRINT_STYLES = `
  @page {
    margin: 16mm 14mm;
  }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
  }
  .pc-doc {
    color: #000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 10.5pt;
    line-height: 1.4;
  }
  .pc-doc * {
    box-sizing: border-box;
  }
  .pc-title {
    font-size: 16pt;
    font-weight: 700;
    line-height: 1.2;
    margin: 0;
  }
  .pc-subtitle {
    color: #444;
    font-size: 9pt;
    margin: 3pt 0 0;
  }
  .pc-details {
    border-bottom: 0.75pt solid #999;
    border-collapse: collapse;
    border-top: 1.5pt solid #000;
    margin: 10pt 0 14pt;
    width: 100%;
  }
  .pc-details th,
  .pc-details td {
    padding: 4pt 0;
    text-align: left;
    vertical-align: top;
  }
  .pc-details th {
    color: #444;
    font-size: 9pt;
    font-weight: 600;
    padding-right: 12pt;
    white-space: nowrap;
    width: 1%;
  }
  .pc-details td {
    overflow-wrap: anywhere;
  }
  .pc-message {
    border-left: 2.5pt solid #000;
    break-inside: avoid;
    margin: 0 0 9pt;
    padding: 1pt 0 1pt 8pt;
    page-break-inside: avoid;
  }
  .pc-message-business,
  .pc-message-ai {
    border-left-color: #999;
  }
  .pc-message-note {
    border-left: 2.5pt dashed #999;
  }
  .pc-message-event {
    border-left: 0;
    color: #444;
    font-style: italic;
    padding-left: 0;
  }
  .pc-meta {
    break-after: avoid;
    font-size: 9pt;
    page-break-after: avoid;
  }
  .pc-author {
    font-size: 10.5pt;
    font-weight: 700;
  }
  .pc-muted {
    color: #444;
  }
  .pc-text {
    margin: 2pt 0 0;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .pc-image {
    break-inside: avoid;
    display: block;
    margin: 4pt 0 0;
    max-height: 80mm;
    max-width: 80mm;
    page-break-inside: avoid;
  }
  .pc-footer {
    border-top: 0.75pt solid #999;
    color: #444;
    font-size: 9pt;
    margin-top: 14pt;
    padding-top: 6pt;
  }
`;

const PrintableConversation = ({ contentRef, record, printedBy }) => {
  const timezone = moment.defaultZone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div ref={contentRef} className="pc-doc">
      <h1 className="pc-title">Conversation with {record.title}</h1>
      <p className="pc-subtitle">
        {[record.businessName, `Printed ${moment().format('LLL')}`, printedBy && `by ${printedBy}`]
          .filter(Boolean)
          .join(' · ')}
      </p>

      <table className="pc-details">
        <tbody>
          {record.customers.map((customer) => (
            <tr key={customer.key}>
              <th scope="row">Customer</th>
              <td>
                {[customer.name, customer.phone, customer.email].filter(Boolean).join(' · ') ||
                  'N/A'}
              </td>
            </tr>
          ))}
          {record.period && (
            <tr>
              <th scope="row">Period</th>
              <td>{record.period}</td>
            </tr>
          )}
          <tr>
            <th scope="row">Times shown in</th>
            <td>{timezone}</td>
          </tr>
        </tbody>
      </table>

      {record.messages.map((message) => (
        <div key={message.key} className={`pc-message pc-message-${message.sender}`}>
          <div className="pc-meta">
            {message.author && <span className="pc-author">{message.author} </span>}
            <span className="pc-muted">
              {[message.role, message.sentAt, message.details].filter(Boolean).join(' · ')}
            </span>
          </div>
          {message.text && <p className="pc-text">{message.text}</p>}
          {message.media?.kind === 'image' && (
            <img className="pc-image" src={message.media.url} alt="Attachment" />
          )}
          {message.media?.kind === 'file' && (
            <p className="pc-text">
              {message.media.label}: {message.media.url}
            </p>
          )}
        </div>
      ))}

      <p className="pc-footer">
        {record.messages.length
          ? `End of conversation · ${record.messages.length} ${
              record.messages.length === 1 ? 'entry' : 'entries'
            }`
          : 'No messages in this conversation'}
      </p>
    </div>
  );
};

export default PrintableConversation;
